import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, delay, catchError } from 'rxjs/operators';
import { StorageService, STORAGE_KEYS } from '../../services/storage.service';
import { ApiService } from '../../services/api.service';
import { Usuario, PerfilUsuario } from '../models/usuario.model';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { AppModeService } from '@core/services/app-mode.service';


/** Ids de perfil del back (sgi_perfiles): 1 admin general, 2 admin congreso, 3 secretaría, 4 cliente, 5 congresista. */
export const ID_PERFIL_SECRETARIA = 3;
export const ID_PERFIL_CONGRESISTA = 5;

/**
 * Respuesta de `UsuariosAdmin/login` y `Usuarios/login` (sobre completo del back, sin pasar por
 * ApiService). `data` es la fila de `sgi_usuarios` con la contraseña enmascarada; `sgiindice` y
 * `congreso` solo vienen informados en `Usuarios/login`.
 */
export interface LoginResponse {
   error?: boolean;
   mensaje?: string;
   token?: string;           // Backend devuelve 'token'
   access_token?: string;    // Alternativa estándar (no usada por el back actual)
   caducidadToken?: number;
   sgiindice?: number | string;
   congreso?: string;
   data?: LoginUsuarioData;
}

/** Fila de usuario tal como la devuelve el back en el login (todo llega como string). */
export interface LoginUsuarioData {
   id?: number | string;
   usrlogin?: string;
   nombre?: string;
   apellidos?: string;
   email?: string;
   nif?: string;
   cif?: string;
   telefono?: string;
   activo?: boolean | number | string;
   idperfil?: number | string;
   nombreperfil?: string;
   sgi_congreso?: string;
   sgiindice?: number | string;
   asistente?: string;
}

/** Respuesta de `UsuariosAdmin/password` y `Usuarios/password` (ApiService ya desempaqueta `data`). */
export interface CambioPasswordResponse {
   mensaje?: string;
}

export interface SolicitarRecuperacionResponse {
   userId: string;
}

export interface CambiarPasswordRequest {
   userId: string;
   password: string;
   code: string;
}

// --- Identificación del usuario público del congreso (registro / login / verificación) ---

/** Respuesta de Usuarios/compruebaemailregistrado. No devuelve datos personales por seguridad. */
export interface CompruebaEmailResponse {
   registrado: boolean;
}

/** Alta de usuario público. Campos fijos mínimos + campos variables del formulario 'reg' del congreso. */
export interface RegistroRequest {
   nombre: string;
   apellidos?: string;
   email: string;
   password: string;
   telefono?: string;
   check_rgpd?: boolean;
   [campo: string]: unknown;
}

/** Respuesta de Usuarios/registro. NO abre sesión: el usuario debe verificar su email. */
export interface RegistroResponse {
   mensaje: string;
   requiereVerificacion?: boolean;
}

/** Respuesta de Usuarios/verificar_email. */
export interface VerificarEmailResponse {
   verificado: boolean;
   mensaje?: string;
}

@Injectable({
   providedIn: 'root',
})
export class AuthService {
   private isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
   private userSubject: BehaviorSubject<Usuario | null> = new BehaviorSubject<Usuario | null>(null);

   constructor(
      private storageService: StorageService,
      private apiService: ApiService,
      private router: Router,
      private http: HttpClient,
      private appModeService: AppModeService,
   ) {
      // Al iniciar, verificamos si hay datos en localStorage
      const savedUser = this.storageService.getItem(STORAGE_KEYS.USUARIO, true);
      if (savedUser) {
         const user: Usuario = savedUser;
         this.userSubject.next(user);
         this.isLoggedInSubject.next(true);
      }
   }

   /**
    * Realiza el login de la zona de administración (superadmin y Secretaría).
    *
    * 1) POST /UsuariosAdmin/login (BD maestra) → superadmin.
    * 2) Si rechaza las credenciales y hay congreso resuelto por URL (entrada por
    *    `<host-congreso>/admin`), POST /Usuarios/login con ese sgiindice → Secretaría (idperfil 3).
    *
    * Endpoint: POST /UsuariosAdmin/login (form-urlencoded con login + password)
    *
    * Respuesta esperada:
    * {
    *   error: boolean,
    *   mensaje: string,
    *   token: string,
    *   sgiindice: number,
    *   caducidadToken: number,
    *   data: { id, nombre, usrlogin, email, activo, ... }
    * }
    */
   login(usuario: string, password: string): Observable<LoginResponse> {
      const body = new URLSearchParams();
      body.set('login', usuario);
      body.set('password', password);

      const httpOptions = {
         headers: new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
         }),
      };

      // Congreso resuelto por URL cuando se entra por `<host-congreso>/admin` (modo admin de
      // congreso, ver app-mode.initializer.ts). En el dominio admin global vale 0.
      const sgiindiceLogin = this.sgiindiceCongreso();

      // Intento 1: superadmin
      return this.http.post<LoginResponse>(`${environment.URL_SERVICIOS}/UsuariosAdmin/login`, body.toString(), httpOptions).pipe(
         tap(response => this.processLoginResponse(response)),
         catchError((err: HttpErrorResponse) => {
            // Si es error de red / CORS (status 0), no es fallo de credenciales:
            // lo propagamos tal cual para no enmascarar el problema real.
            if (err.status === 0) {
               throw err;
            }
            // Fallback: credenciales rechazadas por UsuariosAdmin → probar Usuarios/login
            // (Secretaría). Usuarios/login busca en la BD del congreso, así que necesita el
            // sgiindice: solo se intenta si se ha entrado por la URL de un congreso. Desde el
            // dominio admin global (sin congreso) no hay dónde buscar → se propaga el error.
            if (sgiindiceLogin <= 0) {
               throw err;
            }
            const body2 = new URLSearchParams();
            body2.set('login', usuario);
            body2.set('password', password);
            body2.set('sgiindice', String(sgiindiceLogin));
            return this.http.post<LoginResponse>(`${environment.URL_SERVICIOS}/Usuarios/login`, body2.toString(), httpOptions).pipe(
               tap(response => {
                  const data: LoginUsuarioData = response.data ?? {};
                  if (Number(data.idperfil ?? 0) !== ID_PERFIL_SECRETARIA) {
                     throw new Error('Acceso no autorizado. Usa el login del congreso.');
                  }
                  this.processLoginResponse(response, 'SECRETARIA', sgiindiceLogin);
               })
            );
         })
      );
   }

   // ==========================================================================
   // Identificación del usuario público del congreso (registro / login / verificación)
   // El sgiindice del congreso lo resuelve AppModeService al arrancar (por dominio).
   // El login (Usuarios/login) YA existe en el back y se usa REAL. El mock (usuariosApiMock)
   // cubre solo los 3 endpoints aún Por crear: compruebaemailregistrado, registro, verificar_email.
   // Ver cesta_plan.md (identificación de usuario).
   // ==========================================================================

   /** sgiindice del congreso activo (resuelto por dominio); 0 si aún no resuelto. */
   private sgiindiceCongreso(): number {
      return this.appModeService.congresoData()?.sgiindice ?? 0;
   }

   /**
    * sgiindice para los enlaces que llegan por correo (verificación y reset): el back lo
    * incluye en el propio enlace, y ese valor manda sobre el resuelto por dominio. Así el
    * enlace funciona aunque el front no haya podido resolver el congreso (localhost sin
    * URL_CONGRESO_HOST, dominio distinto…), que dejaría sgiindice=0 y un 400 del filtro.
    */
   private sgiindiceDeEnlace(sgiindiceEnlace?: number | null): number {
      const n = Number(sgiindiceEnlace ?? 0);
      return Number.isFinite(n) && n > 0 ? n : this.sgiindiceCongreso();
   }

   /**
    * Comprueba si un email ya está registrado como usuario del congreso.
    * Sirve para decidir entre pedir contraseña (login) o mostrar el alta. No devuelve datos personales.
    */
   compruebaEmailRegistrado(email: string): Observable<CompruebaEmailResponse> {
      if (environment.usuariosApiMock) {
         // Convención de prueba (solo mock): se consideran dados de alta los valores que
         // contengan "registrado" o que coincidan con un usuario de prueba conocido.
         const valor = email.trim().toLowerCase();
         const usuariosPrueba = ['javi4'];
         const registrado = valor.includes('registrado') || usuariosPrueba.includes(valor);
         return of({ registrado }).pipe(delay(150));
      }
      return this.apiService.post<CompruebaEmailResponse>('Usuarios/compruebaemailregistrado', {
         email,
         sgiindice: this.sgiindiceCongreso(),
      });
   }

   /**
    * Alta de usuario público. No abre sesión: el usuario debe verificar su email
    * (salvo que el congreso tenga la verificación desactivada → requiereVerificacion=false).
    */
   registro(datos: RegistroRequest, urlFront?: string): Observable<RegistroResponse> {
      if (environment.usuariosApiMock) {
         return of<RegistroResponse>({
            mensaje: 'Hemos enviado un email de confirmación a tu dirección. Haz clic en el enlace para activar tu cuenta.',
            requiereVerificacion: true,
         }).pipe(delay(600));
      }
      // El back espera la contraseña bajo la clave `usrpass` (ver Usuarios/registro.bru).
      // url_front = URL de NUESTRA landing a la que debe volver el enlace del correo de
      // verificación (ruta /web/verificar-email). Si no se envía, el back cae en app.baseURL
      // (su propio dominio) y el enlace no vuelve al SPA. Mismo patrón que solicitarRecuperarPassword.
      const { password, ...resto } = datos;
      return this.apiService.post<RegistroResponse>('Usuarios/registro', {
         ...resto,
         usrpass: password,
         ...(urlFront ? { url_front: urlFront } : {}),
         sgiindice: this.sgiindiceCongreso(),
      });
   }

   /**
    * Verifica el token recibido por email (destino del enlace de confirmación).
    */
   verificarEmail(token: string, sgiindiceEnlace?: number | null): Observable<VerificarEmailResponse> {
      if (environment.usuariosApiMock) {
         const verificado = !!token && token !== 'invalido';
         return of<VerificarEmailResponse>({
            verificado,
            mensaje: verificado ? 'Tu cuenta ha sido verificada.' : 'El enlace no es válido o ha caducado.',
         }).pipe(delay(500));
      }
      return this.apiService.post<VerificarEmailResponse>('Usuarios/verificar_email', {
         token,
         sgiindice: this.sgiindiceDeEnlace(sgiindiceEnlace),
      });
   }

   /**
    * Login de usuario normal del congreso. A diferencia del login admin, NO hace fallback a
    * UsuariosAdmin/login: un congresista nunca es superadmin. El sgiindice va en el body
    * (FlujoLoginSGInscripciones.pdf) y se usa HttpClient directo para preservar el envoltorio
    * completo que necesita processLoginResponse.
    */
   loginUsuario(login: string, password: string): Observable<LoginResponse> {
      const sgiindice = this.sgiindiceCongreso();
      const body = new URLSearchParams();
      body.set('login', login);
      body.set('password', password);
      body.set('sgiindice', String(sgiindice));

      const httpOptions = {
         headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      };

      return this.http.post<LoginResponse>(`${environment.URL_SERVICIOS}/Usuarios/login`, body.toString(), httpOptions).pipe(
         tap(response => {
            // Solo abre sesión pública el perfil congresista (5). Secretaría, admins y cliente
            // entran por `<host-congreso>/admin`; si se les dejara pasar aquí, la sesión quedaría
            // etiquetada como CONGRESISTA y llegarían a /web/panel con un perfil que no es el suyo.
            // Espejo de la comprobación idperfil==3 del login de admin.
            const idperfil = Number(response?.data?.idperfil ?? 0);
            if (response?.error !== true && idperfil !== ID_PERFIL_CONGRESISTA) {
               throw new Error('Acceso no autorizado. Este acceso es solo para congresistas.');
            }
            this.processLoginResponse(response, 'CONGRESISTA', sgiindice);
         })
      );
   }

   /**
    * Procesa la respuesta de login y almacena usuario + token + sgiindice.
    * Lanza error si el backend responde con error=true o falta el token.
    */
   private processLoginResponse(response: LoginResponse, perfil: PerfilUsuario = 'ADMIN', sgiindiceLogin?: number): void {
      if (response?.error === true) {
         throw new Error(response.mensaje || 'Login rechazado por el backend');
      }

      const tokenValue: string | undefined = response?.token || response?.access_token;
      if (!tokenValue) {
         throw new Error('No token received from backend');
      }

      const data: LoginUsuarioData = response.data ?? {};
      // sgiindice: el del back si viene (Usuarios/login lo devuelve desde sep-2026), si no el que envió el front.
      const sgiindice = Number(response.sgiindice ?? data.sgiindice ?? sgiindiceLogin ?? 0);
      const userData: Usuario = {
         id: typeof data.id === 'string' ? parseInt(data.id, 10) : (data.id ?? 0),
         login: data.usrlogin || data.email || '',
         nombre: data.nombre ?? '',
         perfil,
         email: data.email || data.usrlogin || '',
         observaciones: '',
         activo: data.activo === '1' || data.activo === 1 || data.activo === true,

         idperfil: data.idperfil == null ? undefined : Number(data.idperfil),
         nombreperfil: data.nombreperfil ?? undefined,
         sgi_congreso: data.sgi_congreso ?? undefined,
         asistente: this.normalizarReferenciaAsistente(data.asistente),
         sgiindice: sgiindice > 0 ? sgiindice : undefined,

         apellidos: data.apellidos ?? '',
         telefono: data.telefono ?? '',
         nif: data.nif ?? data.cif ?? '',
      };

      this.storageService.setItem(STORAGE_KEYS.USUARIO, userData, true);
      this.storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokenValue, true);
      this.storageService.setItem(STORAGE_KEYS.SGIINDICE, sgiindice > 0 ? sgiindice : 0, true);

      if (response.caducidadToken != null) {
         this.storageService.setItem(STORAGE_KEYS.CADUCIDAD_TOKEN, response.caducidadToken, true);
      }

      this.userSubject.next(userData);
      this.isLoggedInSubject.next(true);
   }

   private normalizarReferenciaAsistente(valor: unknown): string | undefined {
      if (typeof valor === 'string') {
         return valor.trim() || undefined;
      }
      return typeof valor === 'number' && Number.isSafeInteger(valor) && valor > 0
         ? String(valor)
         : undefined;
   }

   /**
    * Maneja la lógica cuando una sesión expira: limpia datos y redirige al login que
    * corresponda según el modo de la app (admin → /auth/login, congreso → /web, donde
    * vive la card de acceso).
    */
   handleSessionExpiration(): void {
      this.clearSession();
      this.router.navigate([this.rutaLogin()], { state: { sessionExpired: true } });
   }

   /**
    * Cierra la sesión. Se borra el token y la información de usuario del localStorage.
    */
   logout(): void {
      this.clearSession();
      this.router.navigate([this.rutaLogin()]);
   }

   /**
    * Ruta de login según el modo de la app. Resuelve el TODO del AuthGuard sobre distinguir
    * dominio admin vs congreso. En el sitio público el login vive en la landing (/web),
    * dentro de la card "Accede a tu inscripción".
    */
   private rutaLogin(): string {
      return this.appModeService.isCongreso() ? '/web' : '/auth/login';
   }

   private clearSession(): void {
      this.storageService.removeItem(STORAGE_KEYS.USUARIO);
      this.storageService.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      this.storageService.removeItem(STORAGE_KEYS.SGIINDICE);
      this.storageService.removeItem(STORAGE_KEYS.CADUCIDAD_TOKEN);
      this.userSubject.next(null);
      this.isLoggedInSubject.next(false);
   }

   /**
    * Devuelve el estado de autenticación como booleano, vía BehaviorSubject
    */
   estaLogeadoAsync(): Observable<boolean> {
      return this.isLoggedInSubject.asObservable();
   }

   /**
    * Devuelve el usuario actual como Observable
    */
   getUsuarioAsync(): Observable<Usuario | null> {
      return this.userSubject.asObservable();
   }

   /**
    * Utilidad: permite obtener el token actual de forma directa (sin Observable)
    */
   getToken(): string | null {
      return this.storageService.getItem(STORAGE_KEYS.ACCESS_TOKEN, true);
   }

   /**
    * Indica si la sesion actual es de un superadmin (token con prefijo `sa_`).
    *
    * Util para UI condicional: mostrar selector de congresos, menus de
    * administracion global, etc. El backend ya valida permisos por su cuenta.
    */
   esSuperadmin(): boolean {
      const token = this.getToken();
      return token?.startsWith('sa_') ?? false;
   }

   /**
    * Utilidad: devuelve el sgiindice asociado a la sesión actual.
    * El backend lo exige como campo del body en todas las llamadas autenticadas.
    */
   getSgiindice(): number | null {
      if (this.appModeService.isCongreso()) {
         const sgi = this.appModeService.congresoData()?.sgiindice;
         const num = sgi == null ? null : Number(sgi);
         return num != null && num > 0 ? num : null;
      }
      const user = this.getUsuario();
      if (user?.sgiindice != null && Number(user.sgiindice) > 0) {
         return Number(user.sgiindice);
      }
      const value = this.storageService.getItem(STORAGE_KEYS.SGIINDICE, true);
      const num = value == null ? null : Number(value);
      return num != null && num > 0 ? num : null;
   }

   /**
    * Actualiza el sgiindice en almacenamiento. El login admin devuelve 0;
    * el valor real se obtiene posteriormente vía /Indice/urlcongreso.
    */
   setSgiindice(value: number): void {
      this.storageService.setItem(STORAGE_KEYS.SGIINDICE, value, true);
   }

   /**
   * Utilidad: Devuelve el estado de autenticación como booleano de forma directa (sin Observable)
   */
   estaLogeado(): boolean {
      return this.isLoggedInSubject.getValue();
   }

   /**
    * Utilidad: Devuelve el usuario actual de forma directa (sin Observable)
    */
   getUsuario(): Usuario | null {
      return this.userSubject.getValue();
   }

   /** Completa la referencia propia tras una compra sin cambiar la identidad de la sesión. */
   actualizarReferenciaAsistente(referencia: string, idUsuario: number, congreso: string | number): void {
      const usuario = this.getUsuario();
      if (!usuario || usuario.id !== idUsuario || Number(usuario.idperfil) !== 5
         || String(usuario.sgi_congreso ?? '').trim() !== String(congreso ?? '').trim()
         || usuario.asistente?.trim() || !referencia.trim()) {
         return;
      }
      const actualizado: Usuario = { ...usuario, asistente: referencia.trim() };
      this.storageService.setItem(STORAGE_KEYS.USUARIO, actualizado, true);
      this.userSubject.next(actualizado);
   }

   /**
    * Solicita la recuperación de contraseña enviando un código al email.
    */
   solicitarRecuperacion(email: string): Observable<SolicitarRecuperacionResponse> {
      return this.apiService.post<SolicitarRecuperacionResponse>('Usuarios/solicitar_recuperar_password', {
         email,
         sgiindice: this.sgiindiceCongreso(),
      });
   }

   /**
 * Cambia la contraseña de un administrador autenticado.
 * Endpoint: UsuariosAdmin/password.
 *
 * El token lo inyecta AuthTokenInterceptor.
 */
   cambiarPasswordAdminConToken(
      idUsuario: number,
      oldPassword: string,
      newPassword: string
   ): Observable<CambioPasswordResponse> {
      return this.apiService.post<CambioPasswordResponse>('UsuariosAdmin/password', {
         id: idUsuario,
         oldpassword: oldPassword,
         newpassword: newPassword
      });
   }

   /**
    * Cambia la contraseña de un congresista autenticado.
    * Endpoint: Usuarios/password.
    *
    * El identificador se obtiene del usuario de la sesión y el token
    * lo inyecta AuthTokenInterceptor.
    */
   cambiarPasswordUsuarioConToken(
      oldPassword: string,
      newPassword: string
   ): Observable<CambioPasswordResponse> {
      return this.apiService.post<CambioPasswordResponse>('Usuarios/password', {
         id: this.getUsuario()?.id ?? 0,
         oldpassword: oldPassword,
         newpassword: newPassword
      });
   }

   // ==========================================================================
   // Recuperación de contraseña "in place" (landing del congreso).
   // Backend de Javi (commit 7d01adc). Todos POST form-urlencoded, sin token de sesión.
   // El sgiindice del congreso lo resuelve AppModeService por dominio.
   // ==========================================================================

   /**
    * Paso 1 del reset: pide al backend que envíe el correo con el enlace de recuperación.
    * url_front = la URL de NUESTRA landing, para que el enlace del correo vuelva aquí.
    * El backend responde siempre 200 (mensaje genérico por seguridad).
    */
   solicitarRecuperarPassword(email: string, urlFront: string): Observable<{ enviado: boolean; estado: number }> {
      if (environment.usuariosApiMock) {
         return of({ enviado: true, estado: 1 }).pipe(delay(400));
      }
      return this.apiService.post('Usuarios/solicitar_recuperar_password', {
         email,
         url_front: urlFront,
         sgiindice: this.sgiindiceCongreso(),
      });
   }

   /**
    * Paso 2 del reset: valida el token al aterrizar desde el enlace (sin consumirlo).
    * Si el token no vale, el backend responde 400 → cae en el callback de error.
    */
   comprobarTokenRecuperar(token: string, sgiindiceEnlace?: number | null): Observable<{ valido: boolean }> {
      if (environment.usuariosApiMock) {
         return of({ valido: token !== 'invalido' }).pipe(delay(300));
      }
      return this.apiService.post('Usuarios/comprobar_token_recuperar', {
         token,
         sgiindice: this.sgiindiceDeEnlace(sgiindiceEnlace),
      });
   }

   /**
    * Paso 3 del reset: cambia la contraseña usando el token. El token se marca como usado.
    * Si el token no vale/expiró, el backend responde 400 → callback de error.
    */
   restablecerPassword(token: string, newpassword: string, sgiindiceEnlace?: number | null): Observable<{ restablecido: boolean }> {
      if (environment.usuariosApiMock) {
         return of({ restablecido: true }).pipe(delay(500));
      }
      return this.apiService.post('Usuarios/restablecer_password', {
         token,
         newpassword,
         sgiindice: this.sgiindiceDeEnlace(sgiindiceEnlace),
      });
   }
}
